import { z } from 'zod';
import { ConversationIdSchema } from '../conversation/message_envelope';
import { EnvelopeOrgIdSchema } from '../conversation/org_id';

const SLUG_NAME = /^[a-z][a-z0-9_]{2,49}$/;
// camelCase permitido: o vocabulário canônico do contrato JÁ é camelCase
// (domainHint, actionType, actionId) — minúsculas-only rejeitaria o próprio
// registry e a facade dropava message_sent/card_action em silêncio.
const PROP_KEY = /^[a-z][A-Za-z0-9_]{0,39}$/;

export const PRODUCT_EVENT_SENTINELS = {
  unknownEvent: 'unknown_event',
  unknownCard: 'unknown_card',
  // 'unknown-host' com HÍFEN: o campo `host` valida `^[a-z-]{2,20}$` (sem
  // underscore) e Record/Document reusam o shape — sentinela com underscore
  // produziria documento que o próprio schema do serviço rejeita.
  unknownHost: 'unknown-host',
} as const;

export const ProductEventHostSchema = z.enum(['native', 'iframe']);

/** Plataforma do acesso, carimbada pelo BFF (o app nativo pelo user-agent; o resto é web). */
export const ProductEventPlatformSchema = z.enum(['ios', 'android', 'web']);
export type ProductEventPlatform = z.infer<typeof ProductEventPlatformSchema>;

/** Shape base compartilhado entre input (client) e record (persistido). Extraído
 *  para não duplicar entre ProductEventInputSchema e ProductEventRecordSchema. */
const productEventInputShape = {
  id: z.uuid(),
  name: z.string().regex(SLUG_NAME),
  ts: z.iso.datetime(),
  sessionId: z.uuid(),
  host: z
    .string()
    .regex(/^[a-z-]{2,20}$/)
    .optional(),
  conversationId: ConversationIdSchema.optional(),
  cardType: z.string().regex(SLUG_NAME).optional(),
  props: z.record(
    z.string().regex(PROP_KEY),
    z.union([z.string().max(120), z.number(), z.boolean()]),
  ),
};

/** Item enviado pelo browser. `.strict()`: identidade alegada pelo client é rejeitada. */
export const ProductEventInputSchema = z
  .object(productEventInputShape)
  .strict()
  .refine((e) => Object.keys(e.props).length <= 20, { message: 'props: max 20 keys' });
export type ProductEventInput = z.infer<typeof ProductEventInputSchema>;

/** Envelope do batch browser→BFF — cru DE PROPÓSITO: um item ruim não pode
 *  derrubar o batch; a rota valida item a item com ProductEventInputSchema. */
export const ProductEventBatchSchema = z.object({
  events: z.array(z.unknown()).max(25),
});

/** Item enriquecido (wire BFF→analytics-api): input + carimbos do servidor. */
export const ProductEventRecordSchema = z
  .object({
    ...productEventInputShape,
    sub: z.string().min(1),
    userId: z.string().min(1).optional(),
    /** Cliente efetivo (`business.clients.id`): a seleção de workspace ou a casa do usuário. */
    clientId: z.string().min(1).max(64).optional(),
    platform: ProductEventPlatformSchema.optional(),
    orgId: EnvelopeOrgIdSchema,
    channel: z.literal('app'),
    surface: z.literal('web-chat'),
  })
  .strict()
  .refine((e) => Object.keys(e.props).length <= 20, { message: 'props: max 20 keys' });
export type ProductEventRecord = z.infer<typeof ProductEventRecordSchema>;

export const ProductEventRecordBatchSchema = z.object({
  events: z.array(ProductEventRecordSchema).max(25),
});
